/**
 * Client Operations
 *
 * Platform-agnostic client CRUD operations.
 * These functions can be used by both web and mobile apps.
 *
 * @module @prosperia/shared/data/operations/clients
 */

import {
  getSupabase,
  getCompanyId as sharedGetCompanyId,
  handleSupabaseError,
  withCompanyFilter,
} from '../../api/supabase';

/**
 * Get all clients for a company
 *
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID (optional, will be resolved if not provided)
 * @param {Function} options.getCompanyId - Function to get company ID from context
 * @returns {Promise<Array>} Array of clients or empty array on error
 */
export async function getClients(options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.getCompanyId) {
      companyId = await options.getCompanyId();
    }

    if (!companyId) {
      // Try to get from session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        companyId = await sharedGetCompanyId(session.user);
      }
    }

    if (!companyId) {
      console.log('[clients] No company ID available for getClients');
      return [];
    }

    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[clients] Error fetching clients:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[clients] getClients error:', error);
    handleSupabaseError(error, 'fetch clients');
    return [];
  }
}

/**
 * Get a single client by ID
 *
 * @param {string} id - Client ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Client data or null on error
 */
export async function getClient(id, options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.user) {
      companyId = await sharedGetCompanyId(options.user);
    }

    if (!companyId) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      companyId = await sharedGetCompanyId(session.user);
    }

    const { data, error } = await withCompanyFilter(
      supabase.from('clients').select('*').eq('id', id),
      companyId
    ).single();

    if (error) throw error;
    return data;
  } catch (error) {
    handleSupabaseError(error, 'get client');
    return null;
  }
}

/**
 * Create a new client
 *
 * @param {Object} clientData - Client data to create
 * @param {Object} options - Options object
 * @param {Object} options.user - Pre-resolved user (skips getSession lookup)
 * @param {number} options.insertTimeout - Timeout for insert operation (default: 10000ms)
 * @returns {Promise<Object>} Created client data
 * @throws {Error} If authentication fails or timeout occurs
 */
export async function createClient(clientData, options = {}) {
  const insertTimeoutMs = options.insertTimeout || 10000;

  console.log('[clients] Starting createClient...');

  try {
    const supabase = getSupabase();

    // Resolve created_by from the caller-supplied user, otherwise fall back to
    // getSession. We intentionally do NOT race getSession against a timeout —
    // it shares a navigator lock with other auth operations and can take a few
    // seconds during a concurrent getUser/refresh. RLS enforces auth at the DB
    // layer, so a missing session here just means we can't stamp created_by.
    let userId = options.user?.id || null;

    if (!userId) {
      console.log('[clients] No user passed, calling getSession...');
      const {
        data: { session },
        error: authError,
      } = await supabase.auth.getSession();

      if (authError) {
        console.error('[clients] Auth error:', authError);
        throw new Error(`Authentication error: ${authError.message}`);
      }

      if (!session?.user) {
        throw new Error('User authentication required to create client. Please sign in again.');
      }

      userId = session.user.id;
    }

    // Ensure created_by is set to the authenticated user's ID
    const clientDataWithUser = {
      ...clientData,
      created_by: userId,
    };

    console.log('[clients] Inserting client into database...');

    // Add timeout to the insert operation
    const insertTimeout = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error(`Database insert timed out after ${insertTimeoutMs / 1000} seconds`)),
        insertTimeoutMs
      );
    });

    const { data, error } = await Promise.race([
      supabase.from('clients').insert([clientDataWithUser]).select().single(),
      insertTimeout,
    ]);

    console.log('[clients] Insert result:', { dataId: data?.id, error });

    if (error) {
      console.error('[clients] Database error:', error);
      handleSupabaseError(error, 'create client');
    }

    if (!data) {
      throw new Error('No data returned from database');
    }

    console.log('[clients] ✓ Client created successfully:', data.id);
    return data;
  } catch (error) {
    console.error('[clients] Error in createClient:', error);
    // Re-throw to let the caller handle it
    throw error;
  }
}

/**
 * Get a client by email address
 *
 * @param {string} email - Client email
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Client data or null if not found
 */
export async function getClientByEmail(email, options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.user) {
      companyId = await sharedGetCompanyId(options.user);
    }

    if (!companyId) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      companyId = await sharedGetCompanyId(session.user);
    }

    // Don't use .single() - it causes 406 errors when no results found
    const { data, error } = await withCompanyFilter(
      supabase.from('clients').select('*').eq('email', email),
      companyId
    );

    if (error) throw error;
    // Return first match or null
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    handleSupabaseError(error, 'get client by email');
    return null;
  }
}

const PAGE_SIZE = 1000;
const INSERT_CHUNK_SIZE = 100;

const resolveCompanyId = async (supabase, options) => {
  if (options.companyId) return options.companyId;
  if (options.user) return sharedGetCompanyId(options.user);
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('User not authenticated');
  return sharedGetCompanyId(session.user);
};

/**
 * Lower-cased emails of every existing client in the company, used to detect
 * duplicates during bulk import with one paged read instead of one query per row.
 * Throws on error: an import must not treat a failed read as "no duplicates".
 *
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Set<string>>}
 */
export async function getClientEmails(options = {}) {
  const supabase = getSupabase();
  const companyId = await resolveCompanyId(supabase, options);
  const emails = new Set();

  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await withCompanyFilter(
      supabase.from('clients').select('id, email').not('email', 'is', null),
      companyId
    )
      .order('id')
      .range(from, from + PAGE_SIZE - 1);

    if (error) throw error;
    (data || []).forEach((row) => {
      if (row.email) emails.add(row.email.trim().toLowerCase());
    });
    if (!data || data.length < PAGE_SIZE) break;
  }

  return emails;
}

/**
 * Insert many clients in chunks. A chunk that fails as a whole (e.g. one bad
 * row) is retried row by row so a single failure doesn't sink its neighbours.
 *
 * @param {Object[]} rows - Client rows (already validated/sanitized by the caller)
 * @param {Object} options - Options object
 * @param {Object} options.user - Pre-resolved user (stamps created_by)
 * @param {(done: number, total: number) => void} options.onProgress - Progress callback
 * @returns {Promise<{ created: Object[], failed: { row: Object, error: string }[] }>}
 */
export async function bulkCreateClients(rows, options = {}) {
  const supabase = getSupabase();
  let userId = options.user?.id || null;
  if (!userId) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('User authentication required to create clients. Please sign in again.');
    }
    userId = session.user.id;
  }

  const created = [];
  const failed = [];

  for (let i = 0; i < rows.length; i += INSERT_CHUNK_SIZE) {
    const chunk = rows.slice(i, i + INSERT_CHUNK_SIZE).map((row) => ({ ...row, created_by: userId }));
    const { data, error } = await supabase.from('clients').insert(chunk).select();

    if (!error) {
      created.push(...(data || []));
    } else {
      for (const row of chunk) {
        const { data: single, error: rowError } = await supabase
          .from('clients')
          .insert([row])
          .select()
          .single();
        if (rowError) failed.push({ row, error: rowError.message });
        else created.push(single);
      }
    }

    if (options.onProgress) options.onProgress(Math.min(i + INSERT_CHUNK_SIZE, rows.length), rows.length);
  }

  return { created, failed };
}

/**
 * Update an existing client
 *
 * @param {string} id - Client ID
 * @param {Object} updates - Fields to update
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Updated client data or null on error
 */
export async function updateClient(id, updates, options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.user) {
      companyId = await sharedGetCompanyId(options.user);
    }

    if (!companyId) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      companyId = await sharedGetCompanyId(session.user);
    }

    const { data, error } = await withCompanyFilter(
      supabase.from('clients').update(updates).eq('id', id),
      companyId
    )
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    handleSupabaseError(error, 'update client');
    return null;
  }
}

/**
 * Delete a client
 *
 * @param {string} id - Client ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<boolean>} True on success, false on error
 */
export async function deleteClient(id, options = {}) {
  try {
    const supabase = getSupabase();
    let companyId = options.companyId;

    if (!companyId && options.user) {
      companyId = await sharedGetCompanyId(options.user);
    }

    if (!companyId) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      companyId = await sharedGetCompanyId(session.user);
    }

    const { error } = await withCompanyFilter(
      supabase.from('clients').delete().eq('id', id),
      companyId
    );

    if (error) throw error;
    return true;
  } catch (error) {
    handleSupabaseError(error, 'delete client');
    return false;
  }
}
